<script lang="ts">
  import type { Snippet } from 'svelte';
  import { djb2 } from '../lib/djb2';
  import { hasValidUnlock, isLocalHost, saveUnlock } from '../lib/gate';

  interface Properties {
    passwordHash: number;
    children?: Snippet;
    /** Injectable clock for deterministic tests; defaults to `Date.now`. */
    now?: () => number;
    /** Injectable hostname for deterministic tests; defaults to window.location.hostname. */
    hostname?: string;
  }

  const {
    passwordHash,
    children,
    now: computeNow = Date.now,
    hostname = location.hostname,
  }: Properties = $props();

  // svelte-ignore state_referenced_locally
  const state = $state<{
    isUnlocked: boolean;
    error: string;
    password: string;
    input: HTMLInputElement | undefined;
  }>({
    isUnlocked: isLocalHost(hostname) || hasValidUnlock(localStorage, computeNow()),
    error: '',
    password: '',
    input: undefined,
  });

  $effect(() => {
    if (!state.isUnlocked) state.input?.focus();
  });

  function submit(event: Event): void {
    event.preventDefault();
    if (djb2(state.password) === passwordHash) {
      saveUnlock(localStorage, computeNow());
      state.isUnlocked = true;
      state.error = '';
      return;
    }
    state.error = 'Wrong password.';
    state.input?.select();
  }
</script>

<!-- Password overlay. Renders children when unlocked (either via prior unlock
     in localStorage or after a successful submission); otherwise blocks the UI
     with a modal password form. Purely decorative — API routes are open. -->
{#if state.isUnlocked}
  {@render children?.()}
{:else}
  <div class="gate" role="dialog" aria-modal="true" aria-labelledby="gate-title">
    <form class="gate-card" onsubmit={submit}>
      <span class="gate-title" id="gate-title">Router</span>
      <input
        bind:this={state.input}
        bind:value={state.password}
        class="gate-input"
        type="password"
        autocomplete="current-password"
        placeholder="Password"
        aria-label="Password"
      />
      <button class="gate-btn" type="submit">Unlock</button>
      <span class="gate-error" role="alert">{state.error}</span>
    </form>
  </div>
{/if}

<style>
  .gate {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: rgb(13 17 23 / 97%);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .gate-card {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: 1.5rem 1.75rem;
    width: 320px;
    max-width: 90vw;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .gate-title {
    font-size: var(--font-size-xl);
    color: var(--color-primary);
    font-weight: 600;
  }

  .gate-input {
    background: var(--color-bg);
    border: 1px solid var(--color-border);
    color: var(--color-fg);
    padding: 8px 10px;
    border-radius: var(--radius-sm);
    font-size: 0.95rem;
  }

  .gate-input:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  .gate-btn {
    background: var(--color-success);
    border: none;
    color: #fff;
    padding: 8px 12px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: var(--font-size-md);
  }

  .gate-btn:hover {
    background: var(--color-success-hover);
  }

  .gate-error {
    color: var(--color-accent-red);
    font-size: var(--font-size-sm);
    min-height: 1.1em;
  }
</style>
